import {
  Button,
  Divider,
  Grid,
  Paper,
  Typography,
  Backdrop,
  CircularProgress,
  List,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  DriveFolderUpload as DriveFolderUploadIcon,
  Save,
  Settings,
  SettingsApplications,
  Download,
  ViewList as ViewListIcon,
  Group as GroupIcon,
} from "@mui/icons-material";
import { useState, useEffect, useContext, useRef } from "react";
import EditTabSourceModal from "./EditTabSourceModal";
import {
  saveChangesTooltipText,
  addLookupValueFieldsToTag,
  getTagsWithUnresolvedParent,
  validateTagIdUniqueness,
  readArrayFromStorage,
} from "../helpers/Helpers";
import { applyBulkUpdate, bulkUpdateSummary } from "../helpers/BulkUpdate";
import { buildTagsXml, parseTagsXml } from "../helpers/TagXml";
import {
  buildTagListFromSelection,
  fetchTagListTags,
  getSavedRows,
  getTagsToSave,
  saveTagsAndLayers,
} from "../helpers/TagSave";
import {
  addTagsToGroup,
  deleteGroup,
  removeTagFromGroup,
  renameGroup,
} from "../helpers/TagGroups";
import {
  getTagListById,
  getTagsWithFilter,
  queueTagSync,
} from "../../api/APIHelpers";
import "./EditTabComponent.css";
import { useParams } from "react-router-dom";
import SourceSelection from "./SourceSelection";
import ConfirmDialog from "../../common/components/ConfirmDialog";
import TagDeployer from "./DeployGrid";
import { SharedTagDialog } from "./SharedTagDialog";
import { PreSaveWarningsContent } from "./PreSaveWarningsContent";
import { ConfigureTagList } from "./ConfigureTagList";
import { CollisionMode, SelectCollisionMode } from "./SelectCollisionMode";
import TagComparisonModal from "./TagComparison/TagComparisonModal";
import LoadXMLModal from "./LoadXMLModal";
import { LookupValueContext } from "../../common/contexts/LookupValueContext";
import {
  createTextColumn,
  visibleColumnOrderStorageKey,
  visibleColumnsStorageKey,
} from "../../common/components/DataGrid/DataGridHelper";
import { ManageTagListsModal } from "./ManageTagListsModal";
import TagDataGridWrapper from "./TagDataGridWrapper";
import SelectTagStandard from "./SelectTagStandard";
import localforage from "localforage";
import { AppendToTagList } from "./AppendToTagList";
import BulkUpdateModal from "./BulkUpdateModal";
import GroupPane from "./GroupPane";
import { AlertsContext } from "../../common/contexts/AlertsContext";
import { extractApiErrorMessage } from "../../common/helpers/ErrorUtils";
import { useTagGrid } from "../hooks/useTagGrid";

if (localforage.supports(localforage.INDEXEDDB)) {
  localforage.config({
    driver: localforage.INDEXEDDB,
    name: "Tag Configurator",
  });
}

export default function EditTabComponent() {
  const [openSourceModal, setOpenSourceModal] = useState(false);
  const [openManageTagListsModal, setOpenManageTagListsModal] = useState(false);
  const [openXMLModal, setOpenXMLModal] = useState(false);
  const {
    tags,
    editedTags,
    dirtyTags,
    selectedTags,
    selectedTagRecords,
    replaceGrid,
    restoreSession,
    appendTags,
    insertEmpty,
    duplicate,
    removeRow,
    removeRows,
    updateRow,
    editRows,
    applyEdits,
    reconcileSave,
    reset,
    clear,
    setEditedTags,
    setDirtyTags,
    setSelectedTags,
  } = useTagGrid();
  const [loading, setLoading] = useState(false);
  const [sourceFilter, setSourceFilter] = useState([]);
  const { assetIds } = useParams();
  const { setInfoMessages } = useContext(AlertsContext);
  const [targetAssets, setTargetAssets] = useState([]);
  const [newTagList, setNewTagList] = useState({}); // Information about new tag list to save grid into
  const [assetTargetFilter, setAssetTargetFilter] = useState([]); // Parameters to query for assets to target for deployment

  // Confirmation window
  const [showConfirmDeployDialog, setShowConfirmDeployDialog] = useState(false);
  const [confirmationOnClose, setConfirmationOnClose] = useState(null);
  const [confirmationTitle, setConfirmationTitle] = useState("Confirm");
  const [confirmationText, setConfirmationText] = useState("Confirm");

  const [showDeployGrid, setShowDeployGrid] = useState(false);

  const [tagListConfigOpen, setTagListConfigOpen] = useState(false);
  const [tagListAppendOpen, setTagListAppendOpen] = useState(false);
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false);
  const [currentView, setCurrentView] = useState("grid");

  const [distro, setDistro] = useState("2.7.4");

  const [showTagComparisonDialog, setShowTagComparisonDialog] = useState(false);

  const lookupValues = useContext(LookupValueContext);

  const initialLoad = useRef(true);
  const restoreSessionOnMount = useRef(!assetIds);

  useEffect(() => {
    if (!initialLoad.current) {
      const setStorageAsync = async () => {
        try {
          await localforage.setItem("tags", tags);
          await localforage.setItem("editedTags", editedTags);
          await localforage.setItem("dirtyTags", dirtyTags);
          await localforage.setItem("selectedTags", selectedTags);
          await localforage.setItem("sourceFilter", sourceFilter);
          await localforage.setItem("assetTargetFilter", assetTargetFilter);
        } catch (e) {
          console.error("Error storing grid session in browser");
        }
      };
      setStorageAsync();
    }
  }, [
    dirtyTags,
    editedTags,
    selectedTags,
    tags,
    sourceFilter,
    assetTargetFilter,
  ]);

  useEffect(() => {
    if (!restoreSessionOnMount.current) {
      initialLoad.current = false;
      return;
    }
    const loadStorageAsync = async () => {
      let session;
      try {
        session = {
          tags: await localforage.getItem("tags"),
          editedTags: await localforage.getItem("editedTags"),
          dirtyTags: await localforage.getItem("dirtyTags"),
          selectedTags: await localforage.getItem("selectedTags"),
          sourceFilter: await localforage.getItem("sourceFilter"),
          assetTargetFilter: await localforage.getItem("assetTargetFilter"),
        };
      } catch (e) {
        console.info("No cached grid session found.");
        initialLoad.current = false;
        return;
      }
      restoreSession(session);
      setSourceFilter(session.sourceFilter || []);
      setAssetTargetFilter(session.assetTargetFilter || []);
      initialLoad.current = false;
    };
    loadStorageAsync();
  }, [restoreSession]);

  // Tag ID collision settings
  const [collisionMode, setCollisionMode] = useState(
    localStorage.getItem("collisionMode") || CollisionMode.NoneSet
  );
  const [selectedColumns, setSelectedColumns] = useState(() =>
    readArrayFromStorage(visibleColumnsStorageKey)
  );

  const [tagStandard, setTagStandard] = useState(
    () => Number(localStorage.getItem("tagStandard")) || -1
  );
  const [tagStandardTags, setTagStandardTags] = useState([]);

  const confirmationDialog = (
    <ConfirmDialog
      open={showConfirmDeployDialog}
      onClose={confirmationOnClose}
      title={confirmationTitle}
      content={confirmationText}
    />
  );

  const appendTagsToGrid = (tagsAdded) => appendTags(tagsAdded, collisionMode);

  const bulkUpdateTags = (selectedIndices, fieldInfo, newValue) => {
    const {
      editedTags: updatedTags,
      dirtyTags: updatedDirtyTags,
      updatedCount,
      skippedCount,
      invalidValueCount,
    } = applyBulkUpdate({
      editedTags,
      dirtyTags,
      indices: selectedIndices,
      fieldInfo,
      value: newValue,
      lookupValues,
    });
    applyEdits(updatedTags, updatedDirtyTags);
    setInfoMessages({
      type: "snackbar",
      ...bulkUpdateSummary({
        fieldInfo,
        updatedCount,
        skippedCount,
        invalidValueCount,
      }),
      open: true,
    });
  };

  const [dialogTag, setDialogTag] = useState({});
  const [dialogId, setDialogId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const openTagDialog = (id) => {
    if (dialogOpen) return;
    setDialogTag(editedTags[id]);
    setDialogId(id);
    setDialogOpen(true);
  };

  useEffect(() => {
    if (assetIds && Array.isArray(lookupValues) && lookupValues.length > 0) {
      const assetIdFilter = assetIds.split(",").map((item, idx, ogArr) => {
        return {
          rowId: idx,
          operator: "AND",
          fieldName: "AssetId",
          comparator: "=",
          value: item.trim(),
          addButton: ogArr.length - 1 === idx,
          fieldSource: "Tag",
        };
      });
      setSourceFilter(assetIdFilter);
      let ignore = false;
      const getURLTags = async () => {
        setLoading(true);
        // Cleared before the request so a failed load never leaves other assets' tags showing.
        clear();
        try {
          const result = await getTagsWithFilter(assetIdFilter);
          if (!ignore) {
            const linkedTags = result.data.map((tag) => {
              return addLookupValueFieldsToTag(tag, lookupValues);
            });
            replaceGrid(
              linkedTags,
              linkedTags.map((tag) => ({ ...tag }))
            );
          }
        } catch (error) {
          if (!ignore) {
            console.error(
              "Failed to load tags for the requested assets:",
              error
            );
            setInfoMessages({
              type: "snackbar",
              message: `Failed to load tags for the requested assets: ${extractApiErrorMessage(error)}`,
              severity: "error",
              open: true,
            });
          }
        }
        if (!ignore) {
          setLoading(false);
        }
      };
      getURLTags();
      return () => {
        ignore = true;
        // A newer load turns loading back on; leaving the deep link mid-load must not keep it on.
        setLoading(false);
      };
    }
  }, [assetIds, lookupValues, setInfoMessages, clear, replaceGrid]);

  useEffect(() => {
    if (tagStandard === -1) {
      return;
    }
    let ignore = false;
    const getTagStandardTags = async () => {
      try {
        const tagst = await getTagListById(tagStandard);
        if (!ignore) {
          setTagStandardTags(tagst.data.Tags);
        }
      } catch (error) {
        if (!ignore) {
          console.error("Failed to get tag standard tags:", error);
          setInfoMessages({
            type: "snackbar",
            message: `Failed to get tag standard tags: ${extractApiErrorMessage(error)}`,
            severity: "error",
            open: true,
          });
        }
      }
    };
    getTagStandardTags();
    return () => {
      ignore = true;
    };
  }, [tagStandard, setInfoMessages]);

  const collisionPrompt = () => {
    const closePrompt = async (_) => {
      setShowConfirmDeployDialog(false);
    };
    setConfirmationOnClose(() => closePrompt);
    setConfirmationTitle("Tag ID Collisions");
    setConfirmationText(
      <>
        {
          "You do not have a Tag ID Collision mode selected. Please select one before loading tags into the grid: "
        }
        <List>
          <li>
            <b>Ignore</b> - Tags will be appended to the grid with no action
            taken
          </li>
          <li>
            <b>Overwrite</b> - Tags with a tag ID collision will be overwritten
            with the loaded tag(s)
          </li>
          <li>
            <b>Skip</b> - Do not load tag IDs with a collision into the grid
          </li>
        </List>
      </>
    );
    setShowConfirmDeployDialog(true);
  };

  const ensureCollisionMode = () => {
    if (collisionMode === CollisionMode.NoneSet) {
      collisionPrompt();
      return false;
    }
    return true;
  };

  const updateTagFromEditor = (row) => {
    updateRow(dialogId, row);
    return row;
  };

  const reportTagListLoadError = (error) => {
    console.error("Failed to get tag lists:", error);
    setInfoMessages({
      type: "snackbar",
      message: `Failed to get tag lists: ${extractApiErrorMessage(error)}`,
      severity: "error",
      open: true,
    });
  };

  // Rejects on failure so ManageTagListsModal stays open.
  const getTagLists = async (selectedTagLists) => {
    if (!ensureCollisionMode()) {
      const error = new Error("Collision mode is not set.");
      reportTagListLoadError(error);
      throw error;
    }
    if (!selectedTagLists || selectedTagLists.length === 0) {
      setInfoMessages({
        type: "snackbar",
        message: "Please select at least one tag list.",
        severity: "warning",
        open: true,
      });
      return;
    }
    setLoading(true);
    return fetchTagListTags(selectedTagLists, lookupValues)
      .then((tagListTags) => {
        appendTagsToGrid(tagListTags);
        setInfoMessages({
          open: true,
          severity: "success",
          message: "Successfully appended tag list(s) to grid.",
        });
      })
      .catch((error) => {
        reportTagListLoadError(error);
        throw error;
      })
      .finally(() => setLoading(false));
  };

  const constructNewTagListFromCurrentGrid = (newTagList) =>
    buildTagListFromSelection({
      tagList: newTagList,
      selectedTagRecords,
      lookupValues,
    });

  const reportSaveError = (error) => {
    setInfoMessages({
      type: "snackbar",
      message: `There was an issue saving tags: ${extractApiErrorMessage(error)}`,
      severity: "error",
      open: true,
      autoHideDuration: 10000,
    });
  };

  // Executes when the final save button is clicked after a save target has been selected
  const saveChanges = async () => {
    const tagsToSave = getTagsToSave({ editedTags, dirtyTags, selectedTags });
    if (tagsToSave.length === 0) {
      setInfoMessages({
        type: "snackbar",
        message:
          'No tags were saved. Changes must be made and tags must be selected. New tags must be deployed with the "Select Assets" button.',
        severity: "warning",
        open: true,
        autoHideDuration: 10000,
      });
      return;
    }

    // Save tags & tag layers to the source records
    const proceedWithSave = async () => {
      setInfoMessages({
        type: "snackbar",
        severity: "info",
        message: "Tag changes have been queued for save.",
        open: true,
      });
      const assetIds = tagsToSave
        .filter((t) => t.AssetId != undefined)
        .map((t) => t.AssetId);
      let summary;
      try {
        validateTagIdUniqueness(editedTags);
        summary = await saveTagsAndLayers(tagsToSave);
      } catch (error) {
        reportSaveError(error);
        return;
      }
      const { successCount, failures: failedTagDetails } = summary;

      if (failedTagDetails.length > 0) {
        if (successCount === 0 && failedTagDetails.length === 1) {
          setInfoMessages({
            message: `Failed to save tag: ${failedTagDetails[0].reason}`,
            severity: "error",
            type: "snackbar",
            open: true,
            autoHideDuration: 10000,
          });
        } else {
          const introText =
            successCount === 0
              ? "There was an error saving your changes. The following tags did not update properly:"
              : "There was an error saving some of your changes. The following tags did not update properly:";
          setInfoMessages({
            type: "dialog",
            message: (
              <div>
                {introText}
                <ul>
                  {failedTagDetails.map((detail, index) => (
                    <li key={index}>
                      {`${detail.label}, Reason: ${detail.reason}`}
                    </li>
                  ))}
                </ul>
              </div>
            ),
            title: "Save Errors",
            open: true,
          });
        }
      } else {
        setInfoMessages({
          type: "snackbar",
          message: "Tags saved successfully.",
          severity: "success",
          open: true,
        });
      }

      // When every attempted tag fails, the grid state is left exactly as it was.
      if (successCount > 0) {
        reconcileSave(
          getSavedRows({
            editedTags,
            tags,
            dirtyTags,
            selectedTags,
            failedTags: new Set(failedTagDetails.map((detail) => detail.tag)),
          })
        );
      }

      if (assetIds.length > 0) {
        try {
          await queueTagSync(assetIds);
        } catch (error) {
          reportSaveError(error);
        }
      }
    };

    const syncDisabledTags = tagsToSave.filter((t) => t.SyncToHub === false);
    const unresolvedParentTags = getTagsWithUnresolvedParent(
      tagsToSave,
      editedTags
    );
    if (syncDisabledTags.length > 0 || unresolvedParentTags.length > 0) {
      setConfirmationTitle("Confirm Save");
      setConfirmationText(
        <PreSaveWarningsContent tags={tagsToSave} allTags={editedTags} />
      );
      setConfirmationOnClose(() => async (result) => {
        setShowConfirmDeployDialog(false);
        if (result === true) {
          await proceedWithSave();
        }
      });
      setShowConfirmDeployDialog(true);
    } else {
      await proceedWithSave();
    }
  };

  const reportXMLLoadError = (error) => {
    console.error("Failed to load tags from XML:", error);
    setInfoMessages({
      type: "snackbar",
      message: `Failed to load tags from XML: ${extractApiErrorMessage(error)}`,
      severity: "error",
      open: true,
    });
  };

  const loadTagsFromXML = (xmlString) => {
    try {
      appendTagsToGrid(parseTagsXml(xmlString, lookupValues));
    } catch (error) {
      reportXMLLoadError(error);
    }
  };

  const exportTagsToXML = () => {
    try {
      if (selectedTags.size === 0) {
        setInfoMessages({
          open: true,
          severity: "warning",
          message: "Please select at least one tag to export.",
        });
        return;
      }

      const selectedTagIndexes = Array.from(selectedTags);
      const tagsToExport = selectedTagIndexes.map((index) => editedTags[index]);

      const xmlString = buildTagsXml(tagsToExport, lookupValues);

      const blob = new Blob([xmlString], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tags_export_${new Date().toISOString().split("T")[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setInfoMessages({
        open: true,
        severity: "success",
        message: `Successfully exported ${tagsToExport.length} tag(s) to XML.`,
      });
    } catch (e) {
      console.error(e);
      setInfoMessages({
        open: true,
        severity: "error",
        message: "Error exporting tags to XML.",
      });
    }
  };

  const dropHandler = async (e) => {
    e.preventDefault();
    if (!ensureCollisionMode()) {
      return;
    }
    const droppedFiles = [...(e.dataTransfer.items ?? [])]
      .filter((dropItem) => dropItem.kind === "file")
      .map((dropItem) => dropItem.getAsFile());

    const results = await Promise.allSettled(
      droppedFiles.map(async (file) =>
        parseTagsXml(await file.text(), lookupValues)
      )
    );

    const droppedTags = results.flatMap((result) =>
      result.status === "fulfilled" ? result.value : []
    );
    if (droppedTags.length > 0) {
      appendTagsToGrid(droppedTags);
    }
    results
      .filter((result) => result.status === "rejected")
      .forEach((result) => reportXMLLoadError(result.reason));
  };

  const dragOverHandler = (e) => {
    e.preventDefault();
  };

  const showGroupMessage = (message) => {
    setInfoMessages({
      type: "snackbar",
      message,
      severity: "success",
      open: true,
    });
  };

  /** Applies a group edit and shows `changed`, or shows `unchanged` (if given) when no tag changed. */
  const applyGroupEdit = (
    { editedTags: nextEditedTags, changedIndices },
    { changed, unchanged }
  ) => {
    if (changedIndices.length === 0) {
      if (unchanged) {
        showGroupMessage(unchanged);
      }
      return;
    }
    editRows(nextEditedTags, changedIndices);
    showGroupMessage(changed);
  };

  const handleRemoveTagFromGroup = (tagIndex, groupName) =>
    applyGroupEdit(removeTagFromGroup(editedTags, tagIndex, groupName), {
      changed: `Removed Tag "${editedTags[tagIndex].TagName}" from group "${groupName}".`,
    });

  const handleAddTagToGroup = (tagIndex, groupName) =>
    applyGroupEdit(addTagsToGroup(editedTags, [tagIndex], groupName), {
      changed: `Added Tag "${editedTags[tagIndex].TagName}" to group "${groupName}".`,
    });

  const handleDeleteGroup = (groupName) =>
    applyGroupEdit(deleteGroup(editedTags, groupName), {
      changed: `Deleted group "${groupName}" and removed it from all Tags.`,
      unchanged: `Deleted group "${groupName}".`,
    });

  const handleRenameGroup = (oldGroupName, newGroupName) =>
    applyGroupEdit(renameGroup(editedTags, oldGroupName, newGroupName), {
      changed: `Renamed group "${oldGroupName}" to "${newGroupName}" and updated all Tags.`,
      unchanged: `Renamed group "${oldGroupName}" to "${newGroupName}".`,
    });

  const handleAddMultipleTagsToGroup = (tagIndices, groupName) => {
    const groupEdit = addTagsToGroup(editedTags, tagIndices, groupName);
    const addedCount = groupEdit.changedIndices.length;
    applyGroupEdit(groupEdit, {
      changed: `Added ${addedCount} Tag${addedCount !== 1 ? "s" : ""} to group "${groupName}".`,
    });
  };

  return (
    <>
      <Backdrop
        open={loading}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <CircularProgress />
      </Backdrop>
      <Paper elevation={2} sx={{ p: 2, mt: 1 }}>
        <Typography gutterBottom variant='h5' component='div' color='primary'>
          Tag Configurator
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant='body1'>
              Create, edit, and deploy Tags from the tag configurator.
            </Typography>
          </Grid>
          <Grid container item xs={8} spacing={1} justifyContent={"flex-end"}>
            <Grid item>
              <Button
                variant='contained'
                color='primary'
                startIcon={<SettingsApplications />}
                onClick={() => setOpenManageTagListsModal(true)}>
                Manage Tag Lists
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant='contained'
                color='primary'
                startIcon={<DriveFolderUploadIcon />}
                onClick={() => setOpenSourceModal(true)}>
                Load From Asset
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant='contained'
                color='primary'
                startIcon={<DriveFolderUploadIcon />}
                onClick={() => {
                  if (ensureCollisionMode()) {
                    setOpenXMLModal(true);
                  }
                }}>
                Load From XML
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant='contained'
                color='primary'
                onClick={() => setShowTagComparisonDialog(true)}
                disabled={editedTags.length === 0}>
                Tag Comparison
              </Button>
            </Grid>
          </Grid>
          <Grid
            container
            item
            spacing={1}
            xs={12}
            justifyContent={"space-between"}>
            <Grid item xs={3}>
              <Button
                variant='outlined'
                startIcon={
                  currentView === "grid" ? <GroupIcon /> : <ViewListIcon />
                }
                onClick={() =>
                  setCurrentView(currentView === "grid" ? "groups" : "grid")
                }>
                {currentView === "grid" ? "Manage Tag Groups" : "View Grid"}
              </Button>
            </Grid>
            <Grid container item spacing={1} xs={9} justifyContent={"flex-end"}>
              <Grid item>
                <SelectCollisionMode
                  collisionMode={collisionMode}
                  setCollisionMode={setCollisionMode}
                  showCollisionInfo={() => {
                    collisionPrompt();
                  }}
                />
              </Grid>
              <Grid item>
                <SelectTagStandard
                  tagStandard={tagStandard}
                  setTagStandard={setTagStandard}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ p: 2, my: 1, minHeight: 200 }}>
        {
          <>
            {currentView === "grid" ? (
              <div onDrop={dropHandler} onDragOver={dragOverHandler}>
                <TagDataGridWrapper
                  data={editedTags.map((tag, id) => {
                    return { ...tag, index: id };
                  })}
                  tags={tags.map((tag, id) => {
                    return { ...tag, index: id };
                  })}
                  dirtyTags={dirtyTags}
                  setDirtyTags={setDirtyTags}
                  setData={setEditedTags}
                  addNewEmptyTag={insertEmpty}
                  deleteRow={removeRow}
                  clearGrid={clear}
                  duplicateTag={duplicate}
                  openTagDialog={openTagDialog}
                  resetChanges={reset}
                  distro={distro}
                  setDistro={setDistro}
                  selectedTags={selectedTags}
                  setSelectedTags={setSelectedTags}
                  setInfoMessages={setInfoMessages}
                  tagStandardTags={tagStandardTags}
                  selectedColumns={selectedColumns}
                  setSelectedColumns={setSelectedColumns}
                  onBulkRemoveSelected={removeRows}
                />
              </div>
            ) : (
              <GroupPane
                tags={editedTags}
                setInfoMessages={setInfoMessages}
                addTagToGroup={handleAddTagToGroup}
                removeTagFromGroup={handleRemoveTagFromGroup}
                deleteGroup={handleDeleteGroup}
                renameGroup={handleRenameGroup}
                addMultipleTagsToGroup={handleAddMultipleTagsToGroup}
              />
            )}
            {currentView === "grid" && (
              <>
                <Divider sx={{ my: 2 }} />
                <Stack direction={"row"} spacing={2} justifyContent={"right"}>
                  <Button
                    startIcon={<Download />}
                    onClick={exportTagsToXML}
                    variant='contained'
                    disabled={selectedTags.size === 0}>
                    Export to XML
                  </Button>
                  <Button
                    startIcon={<SettingsApplications />}
                    onClick={() => setBulkUpdateModalOpen(true)}
                    variant='contained'
                    disabled={selectedTags.size === 0}>
                    Bulk Update ({selectedTags.size})
                  </Button>
                  <Button
                    startIcon={<Settings />}
                    onClick={() => setTagListAppendOpen(true)}
                    variant='contained'>
                    Append to Tag List
                  </Button>
                  <Button
                    startIcon={<Settings />}
                    onClick={() => setTagListConfigOpen(true)}
                    variant='contained'>
                    Save New Tag List
                  </Button>
                  <Tooltip title={saveChangesTooltipText}>
                    <Button
                      variant='contained'
                      onClick={async () => {
                        await saveChanges();
                      }}
                      startIcon={<Save />}>
                      Save Changes
                    </Button>
                  </Tooltip>
                  <ConfigureTagList
                    open={tagListConfigOpen}
                    onClose={() => {
                      setTagListConfigOpen(false);
                      setShowDeployGrid(false);
                    }}
                    tagListInfo={newTagList}
                    setTagListInfo={setNewTagList}
                    setInfoMessages={setInfoMessages}
                    constructNewTagListFromCurrentGrid={
                      constructNewTagListFromCurrentGrid
                    }
                  />
                  <AppendToTagList
                    open={tagListAppendOpen}
                    onClose={() => {
                      setTagListAppendOpen(false);
                    }}
                    editedTags={editedTags}
                    selectedTags={selectedTags}
                  />
                </Stack>
              </>
            )}
          </>
        }
      </Paper>
      {editedTags.length > 0 && !newTagList.IsTagStandard && (
        <SourceSelection
          setShowDeployGrid={setShowDeployGrid}
          setAssetTargetFilter={setAssetTargetFilter}
          assetTargetFilter={assetTargetFilter}
          setTargetAssets={setTargetAssets}
          setLoading={setLoading}
          setInfoMessages={setInfoMessages}></SourceSelection>
      )}
      {showDeployGrid ? (
        <TagDeployer
          tags={selectedTagRecords}
          targets={targetAssets}
          setShowConfirmDeployDialog={setShowConfirmDeployDialog}
          setConfirmationOnClose={setConfirmationOnClose}
          setConfirmationTitle={setConfirmationTitle}
          setConfirmationText={setConfirmationText}
          setInfoMessages={setInfoMessages}
        />
      ) : (
        <></>
      )}
      <EditTabSourceModal
        open={openSourceModal}
        onClose={() => {
          setOpenSourceModal(false);
        }}
        setSourceFilter={setSourceFilter}
        sourceFilter={sourceFilter}
        collisionMode={collisionMode}
        collisionPrompt={collisionPrompt}
        setLoading={setLoading}
        appendTagsToGrid={appendTagsToGrid}
      />
      <LoadXMLModal
        open={openXMLModal}
        onClose={() => {
          setOpenXMLModal(false);
        }}
        loadXML={(xml) => {
          setLoading(true);
          loadTagsFromXML(xml);
          setLoading(false);
        }}
        setOpen={setOpenXMLModal}
      />
      <ManageTagListsModal
        open={openManageTagListsModal}
        onClose={() => setOpenManageTagListsModal(false)}
        setInfoMessages={setInfoMessages}
        onLoadTagLists={getTagLists}
      />
      {confirmationDialog}
      {dialogOpen && (
        <SharedTagDialog
          open={dialogOpen}
          setOpen={setDialogOpen}
          tag={dialogTag}
          setTag={setDialogTag}
          updateTag={updateTagFromEditor}
          setLoading={setLoading}
          setInfoMessages={setInfoMessages}
          distro={distro}
          existingTags={editedTags}
        />
      )}
      <BulkUpdateModal
        open={bulkUpdateModalOpen}
        onClose={() => setBulkUpdateModalOpen(false)}
        selectedTags={selectedTags}
        selectedTagRecords={selectedTagRecords}
        onBulkUpdate={bulkUpdateTags}
        editedTags={editedTags}
      />
      <TagComparisonModal
        setUserTags={setEditedTags}
        userTags={editedTags}
        setShowTagComparisonDialog={setShowTagComparisonDialog}
        distro={distro}
        setDistro={setDistro}
        setDirtyTags={setDirtyTags}
        showTagComparisonDialog={showTagComparisonDialog}
        currentCollisionMode={collisionMode}
        currentSetCollisionMode={setCollisionMode}
        currentSelectedColumns={selectedColumns}
        currentSetSelectedColumns={setSelectedColumns}
      />
    </>
  );
}
